import { useState, useMemo, useCallback } from 'react';
import { useReactiveVar } from '@apollo/client';
import { ApolloQueryResult } from '@apollo/client';

import ContentPageActionDropdown from 'approot/shared/content-page/content-page-action-dropdown';
import IconDownload from 'approot/shared/icons/icon-download';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { GetStimulusResourceQuery } from '../shared/stimulus/stimulus.graphql.generated';
import {
  spacing16,
  spacing8,
  ValidSpacings,
} from 'approot/shared/styles/spacing.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import Text from 'approot/shared/styles/typography/text';
import Spinner from 'approot/shared/spinner/spinner';
import useUserData from 'approot/shared/user/user-data';
import {
  openSignInModal,
  openJoinModal,
  openAdminModal,
} from 'approot/global-notifications/global-notification.utils';
import { ModalKey } from 'approot/shared/modal/modal.constants';
import { globalModalVar } from 'approot/global-notifications/global-modals.apollo';
import { useGuidedReadThroughDownload } from './use-guided-read-through-download';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  guidedReadThroughs: Pluck<
    Extract<
      Pluck<GetStimulusResourceQuery, 'getStimulusResource'>,
      { __typename: 'StimulusResourceEBookInteractive' }
    >,
    'guidedReadThroughs'
  >[];
  stimulusResourceId: number;
  marginTop?: ValidSpacings;
  refetch?: () => Promise<ApolloQueryResult<GetStimulusResourceQuery>>;
};

//* Guided Read Through Downloads (GTR) button is used in three areas:
//* 1. stimulus resources interactive page
//* 2. library book detail modal
//* 3. lesson | assessment presentation pages
//* in most cases student view is handled on the route level,
//* for stimulus resources interactive page, a student is allowed to view the resource but not download the GTR,.
//* managing signin state user, type and role on the component level allows component reuse without pushing that responsibility to the parent

const InteractiveDownloadGuidedReadThroughs = ({
  guidedReadThroughs,
  stimulusResourceId,
  marginTop = 0,
  refetch,
}: Props) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const {
    isSignedIn,
    isStudentSignedIn,
    isAlreadySignedInOnce,
    isSchoolTeacher,
  } = useUserData();
  const globalModal = useReactiveVar(globalModalVar);
  const { downloadGuidedReadThrough } = useGuidedReadThroughDownload(
    refetch,
    isSignedIn
  );

  const sortedGuidedReadThroughs = useMemo(() => {
    if (!guidedReadThroughs || guidedReadThroughs.length === 0) {
      return [];
    }
    return [...guidedReadThroughs].sort((a, b) => {
      const nameA = (a.name ?? '').toLowerCase();
      const nameB = (b.name ?? '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [guidedReadThroughs]);

  const handleDownloadGuidedReadThrough = useCallback(
    async (
      guidedReadThrough: Pluck<
        Extract<
          Pluck<GetStimulusResourceQuery, 'getStimulusResource'>,
          { __typename: 'StimulusResourceEBookInteractive' }
        >,
        'guidedReadThroughs'
      >
    ) => {
      if (!isSignedIn) {
        if (globalModal === ModalKey.NONE) {
          isAlreadySignedInOnce ? openSignInModal() : openJoinModal();
        }
        return;
      }

      if (!isSchoolTeacher) {
        openAdminModal();
        return;
      }

      setIsDownloading(true);

      await downloadGuidedReadThrough(
        guidedReadThrough,
        isSignedIn,
        stimulusResourceId
      );

      setIsDownloading(false);
    },
    [
      isSignedIn,
      globalModal,
      isAlreadySignedInOnce,
      isSchoolTeacher,
      downloadGuidedReadThrough,
      stimulusResourceId,
    ]
  );

  const dropdownOptions = useMemo(
    () =>
      sortedGuidedReadThroughs.map(guidedReadThrough => ({
        text: `${guidedReadThrough.name || 'Guided Read Through'}`,
        onClick: () => handleDownloadGuidedReadThrough(guidedReadThrough),
      })),
    [sortedGuidedReadThroughs, handleDownloadGuidedReadThrough]
  );

  if (!guidedReadThroughs || guidedReadThroughs.length === 0) {
    return null;
  }

  const isSingleGuidedReadThrough = sortedGuidedReadThroughs.length === 1;

  return (
    <>
      {!isStudentSignedIn && (
        <Box $maxWidth={334} $gap={spacing16} $marginTop={marginTop}>
          {isSingleGuidedReadThrough ? (
            <SecondaryButton
              onClick={() =>
                handleDownloadGuidedReadThrough(sortedGuidedReadThroughs[0])
              }
            >
              <Box
                $flexDirection="row"
                $alignItems="center"
                $gap={spacing8}
                $flexWrap="nowrap"
              >
                {isDownloading ? (
                  <Spinner />
                ) : (
                  <IconDownload width={24} height={24} />
                )}
                <Text>Download Guided Read Through</Text>
              </Box>
            </SecondaryButton>
          ) : (
            <ContentPageActionDropdown
              buttonLabel="Download Guided Read Through"
              icon={
                isDownloading ? (
                  <Spinner />
                ) : (
                  <IconDownload width={24} height={24} />
                )
              }
              ariaLabel="Download Guided Read Through"
              dropdownOptions={dropdownOptions}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default InteractiveDownloadGuidedReadThroughs;
